import { copyText } from '../copied'
import { postcodeWords } from '../round-config'
import '../get-set-up.css'
import { ApiError, api, changeNow, mutate } from '../api'
import { businessSettings, hasBusinessSettings } from '../business-settings'
import { enquiryAlertsOn, reminderSupport, turnEnquiryAlertsOn } from '../reminders'
import { routeGen } from '../route-state'
import { clear, el, toast } from '../ui'

// Get set up (Liam, 10 Oct 2026: "im worried about onboarding for all these extra feature we need to make set up as
// clear and simple as possible", then "Draw it" to https://claude.ai/artifact/MQDVDGjP7S4PQ1JLt1E3pG and "Go with your
// recomedation"). One list, from More: every extra, whether it is on, and one next step; anything that has stopped
// working comes first. Each set-up is short steps, one at a time, that check themselves as you go:
//   #/get-set-up                  the list
//   #/get-set-up?step=alerts      alerts on this phone: allow, then a test alert ("Did your phone buzz?")
//   #/get-set-up?step=quotes      Quotes by email: the address, where enquiries reach you, then a test that lands
//   #/get-set-up?step=calls       calls and texts: a business number (coming), your own phone, or your own Twilio
// Owners only (the extras are the owner's).

type Status = 'on' | 'todo' | 'attention' | 'locked'
interface Item { key: string; title: string; why: string; status: Status; statusWords: string; go?: { href: string; label: string } }
interface QuotesRow { quotes_by_email: boolean; quotes_address: string | null; has_prices: boolean; version: number }
interface PhoneRow { connected: boolean; status?: string; problem?: string | null; whatsapp_on?: boolean; whatsapp_ready?: boolean }

const onPage = (): boolean => window.location.hash.startsWith('#/get-set-up')
const stepOf = (): string | null => new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('step')

export function mountGetSetUp(): void {
  const step = stepOf()
  if (step === 'alerts') return void alertsSteps()
  if (step === 'quotes') return void quotesSteps()
  if (step === 'calls') return callsChoice()
  void listPage()
}

// The page's Back link is the app's own (navigation.ts moreBackLink: More, or Get set up from a step).
function page(title: string): { root: HTMLElement; body: HTMLElement } {
  const root = document.querySelector<HTMLElement>('#screen')!
  clear(root)
  const body = el('div', { class: 'setup-page' })
  body.append(el('h1', { class: 'setup-title' }, title))
  root.append(body)
  return { root, body }
}

const tick = (): SVGElement => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('aria-hidden', 'true'); svg.setAttribute('class', 'setup-tick')
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
  path.setAttribute('d', 'M5 12.5l4.5 4.5L19 7'); path.setAttribute('fill', 'none'); path.setAttribute('stroke', 'currentColor'); path.setAttribute('stroke-width', '2.8'); path.setAttribute('stroke-linecap', 'round'); path.setAttribute('stroke-linejoin', 'round')
  svg.append(path)
  return svg
}
const doneLine = (words: string): HTMLElement => el('p', { class: 'setup-done-line' }, tick(), words)

// ---- the list ----
async function listPage(): Promise<void> {
  const gen = routeGen
  const { body } = page('Get set up')
  const loading = el('p', { class: 'muted' }, 'Checking what is on…')
  body.append(loading)
  const [quotes, phone, choice, several, alerts] = await Promise.all([
    api<{ row: QuotesRow }>('/api/business/quotes-email').then((r) => r.row).catch(() => null),
    api<{ row: PhoneRow }>('/api/business/phone').then((r) => r.row).catch(() => null),
    api<{ choice: string | null }>('/api/business/calls-choice').then((r) => r.choice).catch(() => null),
    api<{ row: { several_jobs: boolean } }>('/api/business/several-jobs').then((r) => r.row.several_jobs).catch(() => null),
    enquiryAlertsOn().catch(() => false),
  ])
  if (gen !== routeGen || !onPage()) return
  loading.remove()
  const connected = !!phone?.connected
  const failing = connected && phone?.status === 'failed'
  const items: Item[] = [
    failing
      ? { key: 'calls', title: 'Calls and texts', why: phone?.problem ?? 'Your Twilio connection has a problem.', status: 'attention', statusWords: 'Needs attention', go: { href: '#/extra-features', label: 'Fix it' } }
      : connected || choice === 'own_phone'
        ? { key: 'calls', title: 'Calls and texts', why: connected ? 'Texts and WhatsApps from your business number. Replies land in Chats.' : 'Using your own phone: Roundly opens your WhatsApp and Messages with the words written.', status: 'on', statusWords: connected ? 'On' : 'Your phone', go: { href: '#/get-set-up?step=calls', label: 'Change' } }
        : { key: 'calls', title: 'Calls and texts', why: 'Choose how you message and call customers from Roundly.', status: 'todo', statusWords: '', go: { href: '#/get-set-up?step=calls', label: 'Set up' } },
    alerts
      ? { key: 'alerts', title: 'Alerts on this phone', why: 'A buzz for every new enquiry, reply and customer message.', status: 'on', statusWords: 'On' }
      : { key: 'alerts', title: 'Alerts on this phone', why: 'A buzz when someone enquires or replies. About 2 minutes.', status: 'todo', statusWords: '', go: { href: '#/get-set-up?step=alerts', label: 'Set up' } },
    quotes?.quotes_by_email
      ? { key: 'quotes', title: 'Quotes by email', why: 'Website enquiries land in New jobs on their own.', status: 'on', statusWords: 'On' }
      : { key: 'quotes', title: 'Quotes by email', why: 'Website enquiries land in New jobs on their own, details filled in.', status: 'todo', statusWords: '', go: { href: '#/get-set-up?step=quotes', label: 'Set up' } },
    quotes?.has_prices
      ? { key: 'prices', title: 'Your prices', why: 'Quotes add themselves up.', status: 'on', statusWords: 'On', go: { href: '#/prices', label: 'Change' } }
      : { key: 'prices', title: 'Your prices', why: 'Set them once and quotes add themselves up.', status: 'todo', statusWords: '', go: { href: '#/prices', label: 'Set up' } },
    { key: 'messages', title: 'Your messages', why: 'Your own words for reminders, quotes and chases.', status: 'on',
      statusWords: hasBusinessSettings() && businessSettings().message_wording ? 'Your words' : 'Ready', go: { href: '#/your-messages', label: 'Change' } },
    several
      ? { key: 'several', title: 'Several jobs per customer', why: 'Gutters or conservatories on their own dates.', status: 'on', statusWords: 'On' }
      : { key: 'several', title: 'Several jobs per customer', why: 'Gutters or conservatories on their own dates.', status: 'todo', statusWords: '', go: { href: '#/extra-features', label: 'Turn on' } },
    !connected
      ? { key: 'whatsapp', title: 'WhatsApp from Roundly', why: 'Opens up once you have a business number.', status: 'locked', statusWords: 'After calls' }
      : phone?.whatsapp_on
        ? { key: 'whatsapp', title: 'WhatsApp from Roundly', why: 'WhatsApps from your business number.', status: 'on', statusWords: 'On' }
        : { key: 'whatsapp', title: 'WhatsApp from Roundly', why: 'WhatsApps from your business number, approved by WhatsApp.', status: 'todo', statusWords: '', go: { href: '#/extra-features', label: 'Set up' } },
  ]
  const on = items.filter((i) => i.status === 'on').length
  body.append(el('div', { class: 'setup-progress' }, el('span', {}, el('strong', {}, `${on} of ${items.length}`), ' on. Each one is optional.'),
    el('span', { class: 'setup-bar' }, el('span', { class: 'setup-bar-fill', style: `width: ${Math.round((on / items.length) * 100)}%` }))))
  // Anything that stopped working first, then what is left to do, then what is on.
  const order: Record<Status, number> = { attention: 0, todo: 1, locked: 2, on: 3 }
  const list = el('ul', { class: 'setup-list' })
  for (const i of [...items].sort((a, b) => order[a.status] - order[b.status])) {
    const right = i.status === 'todo' && i.go
      ? el('a', { href: i.go.href, class: 'setup-go' }, i.go.label)
      : i.status === 'attention' && i.go ? el('a', { href: i.go.href, class: 'setup-go setup-go-fix' }, i.go.label)
        : el('span', { class: `setup-status setup-status-${i.status}` }, i.statusWords)
    const li = el('li', { class: `setup-item setup-item-${i.status}`, 'data-setup': i.key },
      el('span', { class: 'setup-item-copy' }, el('strong', {}, i.title), el('span', {}, i.why)), right)
    if (i.status === 'on' && i.go) li.append(el('a', { href: i.go.href, class: 'setup-change', 'aria-label': `${i.go.label}: ${i.title}` }, i.go.label))
    list.append(li)
  }
  body.append(list)
  body.append(el('p', { class: 'muted setup-foot' }, 'Something stops working? It moves to the top here and says what to do.'))
}

// ---- steps ----
function stepsFrame(title: string, n: number, of: number): { body: HTMLElement; card: HTMLElement } {
  const { body } = page(title)
  const dots = el('div', { class: 'setup-dots', role: 'img', 'aria-label': n > of ? 'All done' : `Step ${n} of ${of}` })
  for (let k = 1; k <= of; k++) dots.append(el('span', { class: k < n || n > of ? 'setup-dot setup-dot-done' : k === n ? 'setup-dot setup-dot-now' : 'setup-dot' }))
  body.append(dots)
  const card = el('section', { class: 'setup-card' })
  if (n <= of) card.append(el('span', { class: 'setup-step' }, `STEP ${n} OF ${of}`))
  body.append(card)
  return { body, card }
}
function allDone(title: string, words: string, extra?: HTMLElement): void {
  const { card } = stepsFrame(title, 99, 3)
  card.classList.add('setup-card-done')
  card.append(el('span', { class: 'setup-big-tick' }, tick()), el('h2', {}, words))
  if (extra) card.append(extra)
  card.append(el('a', { href: '#/get-set-up', class: 'setup-primary' }, 'Back to Get set up'))
}
const button = (label: string, cls = 'setup-primary'): HTMLButtonElement => el('button', { type: 'button', class: cls }, label) as HTMLButtonElement

// Alerts on this phone: allow, then a test.
let alertsAt = 1
async function alertsSteps(): Promise<void> {
  const gen = routeGen
  const support = reminderSupport()
  const title = 'Alerts on your phone'
  if (support === 'no') {
    const { card } = stepsFrame(title, 1, 2)
    card.append(el('h2', {}, 'Open Roundly on your phone'), el('p', {}, "This browser can't show alerts. Open Roundly on your phone (the Roundly app on iPhone) and come back here."))
    return
  }
  if (support === 'iphone-home-screen') {
    const { card } = stepsFrame(title, 1, 3)
    card.append(el('h2', {}, 'Add Roundly to your Home Screen'), el('p', {}, "On iPhone, alerts need Roundly on your Home Screen, or the Roundly app. Tap Share, then Add to Home Screen, then open Roundly from there and come back here."))
    return
  }
  const on = await enquiryAlertsOn().catch(() => false)
  if (gen !== routeGen || !onPage()) return
  if (alertsAt === 1 && on) alertsAt = 2
  if (alertsAt === 1) {
    const { card } = stepsFrame(title, 1, 2)
    card.append(el('h2', {}, 'Allow alerts'), el('p', {}, 'Your phone asks once. Tap Allow.'))
    const allow = button('Allow alerts')
    allow.addEventListener('click', () => {
      allow.disabled = true
      void turnEnquiryAlertsOn().then((problem) => {
        if (problem) { toast(problem); allow.disabled = false; return }
        alertsAt = 2
        if (onPage()) void alertsSteps()
      })
    })
    card.append(allow)
    return
  }
  if (alertsAt === 2) {
    const { card } = stepsFrame(title, 2, 2)
    card.append(doneLine('Alerts allowed on this phone'), el('h2', {}, 'Send yourself a test'), el('p', {}, 'Your phone should buzz within a few seconds.'))
    const test = button('Send a test alert')
    const after = el('div', { class: 'setup-after' })
    test.addEventListener('click', () => {
      test.disabled = true
      void changeNow<{ sent: number }>('POST', '/api/push/test', {}).then((r) => {
        clear(after)
        if (!r.sent) { after.append(el('p', { class: 'setup-warn' }, "It didn't reach this phone. Turn alerts off and on in New jobs, then try again.")); test.disabled = false; return }
        after.append(el('p', {}, 'Sent. Did your phone buzz?'))
        const yes = button('Yes'), no = button('No', 'setup-secondary')
        yes.addEventListener('click', () => { alertsAt = 3; void alertsSteps() })
        no.addEventListener('click', () => {
          after.append(el('p', { class: 'setup-warn' }, "Open your phone's Settings, then Notifications, then Roundly (or your browser), and turn Allow Notifications on. Then send another test."))
          test.disabled = false
        })
        after.append(el('div', { class: 'setup-pair' }, yes, no))
      }).catch((err: ApiError) => { test.disabled = false; toast(err.message || "Couldn't send the test.") })
    })
    card.append(test, after)
    return
  }
  alertsAt = 1
  allDone(title, "You're set up", el('p', {}, 'A buzz for every new enquiry, reply and customer message, never between 9pm and 7am.'))
}

// Quotes by email: the address, where enquiries reach you, then a test that lands.
let quotesAt = 1
let quotesPlace: 'email' | 'site' | null = null
let quotesBaseline: number | null = null
async function quotesSteps(): Promise<void> {
  const gen = routeGen
  const title = 'Quotes by email'
  const row = await api<{ row: QuotesRow }>('/api/business/quotes-email').then((r) => r.row).catch(() => null)
  if (gen !== routeGen || !onPage()) return
  if (!row) { const { card } = stepsFrame(title, 1, 3); card.append(el('p', {}, "Couldn't check Quotes by email. Try again with signal.")); return }
  if (quotesAt === 1) {
    const { card } = stepsFrame(title, 1, 3)
    card.append(el('h2', {}, 'Get your enquiries address'), el('p', {}, postcodeWords('Every email sent to it becomes an enquiry on New jobs, with their name, number and postcode filled in.')))
    // Turning it on needs prices (a quote adds itself up); already on, the address shows whatever.
    if (!row.has_prices && !(row.quotes_by_email && row.quotes_address)) {
      card.append(el('p', { class: 'setup-warn' }, 'Set your prices first, so a quote can add itself up.'), el('a', { href: '#/prices', class: 'setup-primary' }, 'Set your prices'))
      return
    }
    if (!row.quotes_by_email || !row.quotes_address) {
      const on = button('Give me an address')
      on.addEventListener('click', () => {
        on.disabled = true
        void mutate({ label: 'Turn on quotes by email', method: 'PATCH', path: '/api/business/quotes-email', body: { on: true, version: row.version } }).then((ack) => {
          if (!ack.ok) { on.disabled = false; toast(ack.error ?? "Couldn't turn it on."); return }
          if (onPage()) void quotesSteps()
        })
      })
      card.append(on)
      return
    }
    const address = `${row.quotes_address}@roundly.work`
    const copy = button('Copy', 'setup-secondary setup-copy')
    copy.addEventListener('click', () => { void copyText(address, { button: copy, what: 'address' }) })
    const next = button('Next')
    next.addEventListener('click', () => { quotesAt = 2; void quotesSteps() })
    card.append(el('div', { class: 'setup-address' }, el('span', {}, address), copy), next)
    return
  }
  if (quotesAt === 2) {
    const { card } = stepsFrame(title, 2, 3)
    card.append(el('h2', {}, 'Where do enquiries reach you now?'))
    const choice = (key: 'email' | 'site', heading: string, detail: string): HTMLButtonElement => {
      const b = el('button', { type: 'button', class: `setup-choice${quotesPlace === key ? ' setup-choice-on' : ''}`, 'aria-pressed': String(quotesPlace === key) }, el('strong', {}, heading), el('span', {}, detail)) as HTMLButtonElement
      b.addEventListener('click', () => { quotesPlace = key; void quotesSteps() })
      return b
    }
    card.append(choice('email', 'In my email', 'Gmail, Outlook or another mailbox'), choice('site', 'Straight from my website', 'Shopify, Wix, WordPress and the like'))
    const address = `${row.quotes_address}@roundly.work`
    if (quotesPlace === 'email') card.append(el('p', { class: 'setup-how' }, `In your email, make a rule: emails from your website's form go on to ${address}. In Gmail: Settings, Filters, "Create a new filter", from your website's address, then "Forward it to" ${address}.`))
    if (quotesPlace === 'site') card.append(el('p', { class: 'setup-how' }, `In your website's settings, put ${address} where contact form emails go (Shopify: Settings, Notifications, Staff notifications; others: the form's "send to" address).`))
    if (quotesPlace) {
      const next = button('Done that')
      next.addEventListener('click', () => { quotesAt = 3; quotesBaseline = null; void quotesSteps() })
      card.append(next)
    }
    return
  }
  if (quotesAt === 3) {
    const { card } = stepsFrame(title, 3, 3)
    card.append(el('h2', {}, 'Send a test enquiry'), el('p', {}, 'Fill in your own website form, or email your address, with "Test" as the name. Roundly watches for it here.'))
    const status = el('p', { class: 'setup-watching' }, 'Watching for your test…')
    card.append(status)
    // The newest enquiry now; anything after it that came by email is the test.
    const leads = (): Promise<{ id: number; name: string; came_by: string | null }[]> => api<{ leads: { id: number; name: string; came_by: string | null }[] }>('/api/leads').then((r) => r.leads)
    if (quotesBaseline === null) quotesBaseline = (await leads().catch(() => [])).reduce((n, l) => Math.max(n, l.id), 0)
    const look = async (): Promise<void> => {
      if (gen !== routeGen || !onPage() || quotesAt !== 3) return
      const found = (await leads().catch(() => [])).find((l) => l.id > (quotesBaseline ?? 0) && l.came_by === 'email')
      if (gen !== routeGen || !onPage() || quotesAt !== 3) return
      if (!found) { window.setTimeout(() => void look(), 5000); return }
      clear(status); status.className = 'setup-done-line'; status.append(tick(), `Got it: "${found.name}" is on New jobs.`)
      const finish = button('Finish')
      finish.addEventListener('click', () => { quotesAt = 4; void quotesSteps() })
      card.append(finish)
    }
    void look()
    return
  }
  quotesAt = 1
  allDone(title, 'Enquiries come in on their own', el('p', {}, 'Slide the test enquiry away on New jobs whenever you like.'))
}

// Calls and texts: three ways, one tap each.
function callsChoice(): void {
  const { body } = page('Calls and texts')
  body.append(el('p', { class: 'muted' }, 'How do you want to message and call customers from Roundly?'))
  const card = (heading: string, detail: string, tag: string | null, act: HTMLElement): HTMLElement =>
    el('section', { class: 'setup-card setup-way' }, el('span', { class: 'setup-way-head' }, el('strong', {}, heading), tag ? el('span', { class: 'setup-tag' }, tag) : ''), el('p', {}, detail), act)
  const soon = el('span', { class: 'setup-status setup-status-locked' }, 'Coming soon')
  const own = button('Use my own phone', 'setup-secondary')
  own.addEventListener('click', () => {
    own.disabled = true
    void changeNow('POST', '/api/business/calls-choice', { choice: 'own_phone' }).then(() => { toast('Done: Roundly opens your WhatsApp and Messages with the words written.'); window.location.hash = '#/get-set-up' })
      .catch((err: ApiError) => { own.disabled = false; toast(err.message || "Couldn't save that.") })
  })
  const twilio = el('a', { href: '#/extra-features', class: 'setup-secondary' }, 'Connect my Twilio account')
  twilio.addEventListener('click', () => { void changeNow('POST', '/api/business/calls-choice', { choice: 'own_twilio' }).catch(() => undefined) })
  body.append(
    card('Get a business number', 'A new number for your business. Texts and WhatsApps go from it, replies land in Chats, and calls ring in Roundly. On your Roundly bill.', 'Recommended', soon),
    card('Keep your own number', "Free, nothing to set up. Roundly opens your own WhatsApp and Messages with the words written; replies come to your phone.", null, own),
    card('Use your own Twilio account', 'If you already have one. You pay Twilio directly.', null, twilio),
  )
}
